import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  WalletIcon, GrowthIcon, PlusIcon, ArrowRightIcon,
  CalendarIcon, DeviceIcon, FeesIcon, ReceiptIcon,
  ShieldIcon, Building2Icon, CheckIcon,
} from "./icons";
import {
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  CreditCard, Smartphone, Building, Plus, MoreHorizontal,
  ChevronRight, Sparkles, Zap, Bell, Download,
  PieChart, BarChart3, Wallet, Receipt, CalendarClock,
  AlertCircle, CheckCircle2, Clock, Filter, Search,
  ArrowLeftRight, FileText, Send,
  GraduationCap, Bus, Apple, Shirt, PiggyBank, Target,
  Split, Repeat, ShieldCheck, Flame, Users2,
} from "lucide-react";
import { C, ACCENT_GRADIENT, BRAND_GRADIENT, type Student, type FeeTerm, type ParentWallet, type Ledger, childColor } from "./tokens";
import { PremiumCard, SectionHeader, MiniBar, Pill, Avatar, ProgressRing, StatTile } from "./primitives";
import { fmtUGX, shortDateTime } from "@/lib/format";
import { FamilyTreasury } from "@/features/family-treasury";
import { TriSpherePayTopUp } from "@/components/schoolos-pay-topup";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const EASE = [0.4, 0, 0.2, 1] as const;

function useCountUp(target: number, duration = 1000): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

type StudentWallet = {
  id: string;
  owner_id: string;
  balance_ugx: number;
  wallet_type: string;
};

// ── Transfer Dialog ──
type Bucket = "fees" | "transport" | "canteen" | "uniform" | "treasury" | "savings" | "emergency";
const BUCKETS: { k: Bucket; label: string; icon: any; tone: string }[] = [
  { k: "fees",      label: "School Fees",   icon: GraduationCap, tone: "#7B1416" },
  { k: "transport", label: "Transport",     icon: Bus,           tone: "#8B6F47" },
  { k: "canteen",   label: "Meals / Canteen",icon: Apple,         tone: "#A42022" },
  { k: "uniform",   label: "Uniform",       icon: Shirt,         tone: "#C4A882" },
  { k: "treasury",  label: "Family Treasury",icon: Wallet,        tone: "#4A4036" },
  { k: "savings",   label: "Savings Goal",  icon: PiggyBank,     tone: "#6B5535" },
  { k: "emergency", label: "Emergency",     icon: Flame,         tone: "#B33A1A" },
];

type Scenario = {
  id: string; label: string; icon: any; tone: string; desc: string;
  preset: { sid: string | null; bucket: Bucket; amount: number }[];
};

function buildScenarios(students: Student[], fees: FeeTerm[]): Scenario[] {
  const sc: Scenario[] = [];
  for (const s of students) {
    const f = fees.find(x => x.student_id === s.id);
    const outstanding = f ? Math.max(0, f.total_ugx - f.paid_ugx) : 0;
    sc.push({
      id: `fees-${s.id}`, label: `Pay ${s.full_name.split(" ")[0]}'s Fees`, icon: GraduationCap, tone: "#7B1416",
      desc: outstanding > 0 ? `${fmtUGX(outstanding)} outstanding` : "Fees settled",
      preset: [{ sid: s.id, bucket: "fees", amount: outstanding > 0 ? outstanding : 100000 }],
    });
    sc.push({
      id: `meals-${s.id}`, label: `Fund ${s.full_name.split(" ")[0]}'s Meals`, icon: Apple, tone: "#A42022",
      desc: "Canteen wallet top-up",
      preset: [{ sid: s.id, bucket: "canteen", amount: 50000 }],
    });
    sc.push({
      id: `transport-${s.id}`, label: `Top Up ${s.full_name.split(" ")[0]}'s Transport`, icon: Bus, tone: "#8B6F47",
      desc: s.bus_route ?? "Bus fund",
      preset: [{ sid: s.id, bucket: "transport", amount: 30000 }],
    });
  }
  sc.push({
    id: "emergency", label: "Emergency Transfer", icon: Flame, tone: "#B33A1A",
    desc: "Immediate funds to first child",
    preset: students[0] ? [{ sid: students[0].id, bucket: "emergency", amount: 50000 }] : [],
  });
  sc.push({
    id: "savings", label: "Save for Next Term", icon: PiggyBank, tone: "#6B5535",
    desc: "Build a savings goal",
    preset: [{ sid: null, bucket: "savings", amount: 200000 }],
  });
  sc.push({
    id: "rebalance", label: "Rebalance Household", icon: Split, tone: "#4A4036",
    desc: "Distribute across all children",
    preset: students.map(s => ({ sid: s.id, bucket: "treasury" as Bucket, amount: 50000 })),
  });
  return sc;
}

type SplitLine = { sid: string | null; studentName: string; tone: string; initials: string; bucket: Bucket; amount: number };

function TransferDialog({ open, onClose, parentWallet, students, studentWallets, fees }: {
  open: boolean; onClose: () => void;
  parentWallet: ParentWallet | null | undefined;
  students: Student[];
  studentWallets: StudentWallet[];
  fees: FeeTerm[];
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"scenarios" | "split" | "manual">("scenarios");
  const [fromId, setFromId] = useState<string>("parent");
  const [splitLines, setSplitLines] = useState<SplitLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [executing, setExecuting] = useState(false);

  const parentBalance = parentWallet?.balance_ugx ?? 0;

  // Group students by school for multi-school awareness
  const schoolGroups = useMemo(() => {
    const map = new Map<string, { school: string; students: Student[] }>();
    for (const s of students) {
      const sch = s.school_name ?? "Other";
      if (!map.has(sch)) map.set(sch, { school: sch, students: [] });
      map.get(sch)!.students.push(s);
    }
    return Array.from(map.values());
  }, [students]);

  const scenarios = useMemo(() => buildScenarios(students, fees), [students, fees]);

  // Wallets for manual mode
  const wallets = [
    { id: "parent", label: "Family Treasury", balance: parentBalance, owner_id: user?.id ?? "", owner_type: "parent" as const },
    ...studentWallets.map(w => {
      const s = students.find(s => s.id === w.owner_id);
      return { id: w.id, label: s?.full_name ?? "Student", balance: w.balance_ugx, owner_id: w.owner_id, owner_type: "student" as const };
    }),
  ];
  const studentsWithoutWallets = students.filter(s => !studentWallets.some(w => w.owner_id === s.id));
  const destOptions = [
    ...wallets.filter(w => w.id !== fromId),
    ...studentsWithoutWallets.map(s => ({ id: s.id, label: `${s.full_name} (new)`, balance: 0, owner_id: s.id, owner_type: "student" as const })),
  ];

  const splitTotal = splitLines.reduce((s, l) => s + l.amount, 0);

  function applyScenario(sc: Scenario) {
    setMode("split");
    setSplitLines(sc.preset.map(p => {
      const s = students.find(x => x.id === p.sid);
      return {
        sid: p.sid,
        studentName: s?.full_name ?? "Family Treasury",
        tone: s?.color_hex ?? "#4A4036",
        initials: s?.initials ?? "★",
        bucket: p.bucket,
        amount: p.amount,
      };
    }));
  }

  function addSplitLine(sid: string | null, bucket: Bucket) {
    const s = students.find(x => x.id === sid);
    setSplitLines(prev => [...prev, {
      sid, studentName: s?.full_name ?? "Family Treasury",
      tone: s?.color_hex ?? "#4A4036", initials: s?.initials ?? "★",
      bucket, amount: 10000,
    }]);
  }

  function updateSplitLine(idx: number, patch: Partial<SplitLine>) {
    setSplitLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  }

  function removeSplitLine(idx: number) {
    setSplitLines(prev => prev.filter((_, i) => i !== idx));
  }

  async function ensureStudentWallet(studentId: string): Promise<string> {
    const existing = studentWallets.find(w => w.owner_id === studentId);
    if (existing) return existing.id;
    const { data, error } = await supabase.from("wallets")
      .insert({ owner_id: studentId, owner_type: "student", balance_ugx: 0, wallet_type: "student" })
      .select().single();
    if (error) throw error;
    return data.id;
  }

  async function executeSplit() {
    if (!user) return;
    if (splitLines.length === 0) { toast.error("Add at least one allocation"); return; }
    if (splitTotal < 500) { toast.error("Minimum total is UGX 500"); return; }
    if (parentBalance < splitTotal) { toast.error(`Insufficient balance — you need ${fmtUGX(splitTotal)} but have ${fmtUGX(parentBalance)}`); return; }
    setExecuting(true);
    try {
      const wtx = `WTX-${Date.now().toString(36).toUpperCase()}`;
      // Debit parent wallet
      await supabase.from("wallets")
        .update({ balance_ugx: parentBalance - splitTotal })
        .eq("owner_id", user.id).eq("owner_type", "parent");

      // Fan out each split line
      for (const line of splitLines) {
        if (line.amount <= 0) continue;
        const b = BUCKETS.find(x => x.k === line.bucket)!;
        if (line.sid) {
          // Credit student wallet (auto-create if needed)
          const wId = await ensureStudentWallet(line.sid);
          const { data: sw } = await supabase.from("wallets").select("balance_ugx").eq("id", wId).single();
          await supabase.from("wallets")
            .update({ balance_ugx: (sw?.balance_ugx ?? 0) + line.amount })
            .eq("id", wId);
        }
        // Ledger entry
        await supabase.from("parent_ledger").insert({
          guardian_id: user.id, student_id: line.sid,
          kind: line.bucket, direction: "debit",
          title: `${line.studentName} · ${b.label}`,
          subtitle: wtx, amount_ugx: line.amount, ref: wtx,
        });
      }
      toast.success(`${fmtUGX(splitTotal)} allocated across ${splitLines.length} wallet${splitLines.length > 1 ? "s" : ""}`);
      qc.invalidateQueries();
      onClose();
      setSplitLines([]);
    } catch (e: any) {
      toast.error(e?.message ?? "Transfer failed");
    } finally {
      setExecuting(false);
    }
  }

  async function executeManual(fromId: string, toId: string, amount: number) {
    if (!user) return;
    if (!toId) { toast.error("Select a destination"); return; }
    if (amount < 500) { toast.error("Minimum transfer is UGX 500"); return; }
    const fromW = wallets.find(w => w.id === fromId);
    if ((fromW?.balance ?? 0) < amount) { toast.error("Insufficient balance"); return; }
    setSubmitting(true);
    try {
      const wtx = `WTX-${Date.now().toString(36).toUpperCase()}`;
      let destWalletId = toId;
      if (toId !== "parent") {
        const existing = studentWallets.find(w => w.id === toId);
        if (!existing) destWalletId = await ensureStudentWallet(toId);
      }
      // Debit source
      if (fromId === "parent") {
        await supabase.from("wallets").update({ balance_ugx: parentBalance - amount }).eq("owner_id", user.id).eq("owner_type", "parent");
      } else {
        const sw = studentWallets.find(w => w.id === fromId);
        if (sw) await supabase.from("wallets").update({ balance_ugx: sw.balance_ugx - amount }).eq("id", sw.id);
      }
      // Credit destination
      if (toId === "parent") {
        await supabase.from("wallets").update({ balance_ugx: parentBalance + amount }).eq("owner_id", user.id).eq("owner_type", "parent");
      } else {
        const dw = studentWallets.find(w => w.id === toId);
        if (dw) {
          await supabase.from("wallets").update({ balance_ugx: dw.balance_ugx + amount }).eq("id", dw.id);
        } else {
          await supabase.from("wallets").update({ balance_ugx: amount }).eq("id", destWalletId);
        }
      }
      const fromLabel = fromW?.label ?? "Wallet";
      const toW = destOptions.find(w => w.id === toId);
      const toLabel = toW?.label ?? "Wallet";
      await supabase.from("parent_ledger").insert([
        { guardian_id: user.id, kind: "transfer", direction: "debit", title: `Transfer to ${toLabel}`, subtitle: wtx, amount_ugx: amount, ref: wtx },
        { guardian_id: user.id, kind: "transfer", direction: "credit", title: `Transfer from ${fromLabel}`, subtitle: wtx, amount_ugx: amount, ref: wtx },
      ]);
      toast.success(`${fmtUGX(amount)} transferred to ${toLabel}`);
      qc.invalidateQueries();
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Transfer failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
        className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden p-6 text-white" style={{ background: "#1C1917" }}>
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-20" style={{ background: "white" }} />
          <div className="absolute -left-16 -bottom-16 h-40 w-40 rounded-full opacity-10" style={{ background: "white" }} />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur">
                <Sparkles className="h-3 w-3" /> Family Money Orchestrator
              </div>
              <h3 className="mt-3 text-2xl font-bold" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Manage your family's education budget</h3>
              <p className="mt-1 text-xs text-white/70">Available: <span className="font-bold text-white">{fmtUGX(parentBalance)}</span></p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
          </div>
          {/* Mode tabs */}
          <div className="relative mt-4 flex gap-1.5">
            {([
              { k: "scenarios", label: "Quick Actions", icon: Zap },
              { k: "split", label: "Split Transfer", icon: Split },
              { k: "manual", label: "Manual", icon: ArrowLeftRight },
            ] as { k: typeof mode; label: string; icon: any }[]).map(t => (
              <button key={t.k} onClick={() => setMode(t.k)}
                className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition ${mode === t.k ? "bg-white text-[#1C1917]" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Scenarios mode ── */}
        {mode === "scenarios" && (
          <div className="p-6">
            {/* Multi-school banner */}
            {schoolGroups.length > 1 && (
              <div className="mb-4 flex items-center gap-2 rounded-xl p-3" style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}22` }}>
                <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: C.accent }} />
                <p className="text-xs" style={{ color: C.textMid }}>
                  Your children attend <b>{schoolGroups.length} schools</b> — {schoolGroups.map(g => g.school).join(", ")}. Funds are routed to each child's wallet at their respective school.
                </p>
              </div>
            )}
            <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: C.textMuted }}>What do you want to do?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {scenarios.map(sc => (
                <motion.button key={sc.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => applyScenario(sc)}
                  className="flex items-center gap-3 rounded-2xl p-3.5 text-left transition" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg }}>
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${sc.tone}14` }}>
                    <sc.icon className="h-5 w-5" style={{ color: sc.tone }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate" style={{ color: C.text }}>{sc.label}</p>
                    <p className="text-[11px] truncate" style={{ color: C.textMuted }}>{sc.desc}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0" style={{ color: C.textLight }} />
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* ── Split mode ── */}
        {mode === "split" && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Source: Family Treasury</p>
                <p className="text-sm font-bold" style={{ color: C.text }}>{fmtUGX(parentBalance)} available</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Total allocation</p>
                <p className="text-lg font-bold" style={{ color: splitTotal > parentBalance ? C.red : C.accent }}>{fmtUGX(splitTotal)}</p>
              </div>
            </div>

            {/* School group context for each line */}
            <div className="space-y-2 mb-3 max-h-[320px] overflow-y-auto pr-1">
              {splitLines.map((line, i) => {
                const b = BUCKETS.find(x => x.k === line.bucket)!;
                const s = students.find(x => x.id === line.sid);
                return (
                  <div key={i} className="flex items-center gap-2 rounded-xl p-2.5" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg }}>
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white" style={{ background: line.tone }}>{line.initials}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold" style={{ color: C.text }}>{line.studentName}</div>
                      {s?.school_name && <div className="text-[10px] truncate" style={{ color: C.textMuted }}>{s.school_name}</div>}
                      <select value={line.bucket} onChange={e => updateSplitLine(i, { bucket: e.target.value as Bucket })}
                        className="mt-0.5 w-full rounded border bg-white px-1 py-0.5 text-[10px]" style={{ borderColor: C.cardBorder, color: C.textMid }}>
                        {BUCKETS.map(opt => <option key={opt.k} value={opt.k}>{opt.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-1">
                      <input type="number" value={line.amount || ""} onChange={e => updateSplitLine(i, { amount: Number(e.target.value) })}
                        placeholder="0" className="w-20 rounded-lg px-2 py-1.5 text-right text-xs font-bold" style={{ border: `1px solid ${C.cardBorder}`, color: C.text }} />
                      <button onClick={() => removeSplitLine(i)} className="p-1 rounded-lg hover:bg-[#E2D6C0]"><svg className="h-3.5 w-3.5" style={{ color: C.textMuted }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                    </div>
                  </div>
                );
              })}
              {splitLines.length === 0 && (
                <div className="rounded-xl border border-dashed p-8 text-center" style={{ borderColor: C.border }}>
                  <Split className="h-8 w-8 mx-auto mb-2" style={{ color: C.textLight }} />
                  <p className="text-xs" style={{ color: C.textMuted }}>Add allocation lines below to split a transfer across wallets</p>
                </div>
              )}
            </div>

            {/* Add line buttons */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {students.map(s => (
                <button key={s.id} onClick={() => addSplitLine(s.id, "treasury")}
                  className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition" style={{ border: `1px solid ${C.cardBorder}`, color: C.textMid }}>
                  <Plus className="h-3 w-3" /> {s.full_name.split(" ")[0]}
                </button>
              ))}
              <button onClick={() => addSplitLine(null, "savings")}
                className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition" style={{ border: `1px solid ${C.cardBorder}`, color: C.textMid }}>
                <Plus className="h-3 w-3" /> Family
              </button>
            </div>

            {/* Quick fill */}
            <div className="flex flex-wrap gap-1.5 mb-5">
              <span className="text-[10px] uppercase tracking-wider" style={{ color: C.textMuted }}>Quick fill total ·</span>
              {[50000, 100000, 200000, 500000].map(v => (
                <button key={v} onClick={() => { if (splitLines.length > 0) { const per = Math.round(v / splitLines.length); setSplitLines(prev => prev.map(l => ({ ...l, amount: per }))); } }}
                  className="rounded-lg px-2.5 py-1 text-[10px] font-bold" style={{ border: `1px solid ${C.cardBorder}`, color: C.textMuted }}>{fmtUGX(v)}</button>
              ))}
            </div>

            <button onClick={executeSplit} disabled={executing || splitLines.length === 0 || splitTotal < 500 || splitTotal > parentBalance}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: "#1C1917" }}>
              {executing ? (
                <><motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} className="h-4 w-4 rounded-full" style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff" }} /> Allocating…</>
              ) : (
                <><Split className="h-4 w-4" /> Allocate {fmtUGX(splitTotal)} across {splitLines.length} wallet{splitLines.length !== 1 ? "s" : ""}</>
              )}
            </button>
            {splitTotal > parentBalance && (
              <p className="mt-2 text-center text-xs font-semibold" style={{ color: C.red }}>Insufficient balance by {fmtUGX(splitTotal - parentBalance)}</p>
            )}
          </div>
        )}

        {/* ── Manual mode ── */}
        {mode === "manual" && (
          <ManualTransfer
            fromId={fromId} setFromId={setFromId}
            wallets={wallets} destOptions={destOptions}
            parentBalance={parentBalance}
            submitting={submitting}
            onExecute={executeManual}
            onClose={onClose}
          />
        )}
      </motion.div>
    </motion.div>
  );
}

function ManualTransfer({ fromId, setFromId, wallets, destOptions, parentBalance, submitting, onExecute, onClose }: {
  fromId: string; setFromId: (v: string) => void;
  wallets: { id: string; label: string; balance: number; owner_id: string; owner_type: string }[];
  destOptions: { id: string; label: string; balance: number; owner_id: string; owner_type: string }[];
  parentBalance: number; submitting: boolean;
  onExecute: (fromId: string, toId: string, amount: number) => void;
  onClose: () => void;
}) {
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState(0);
  const fromWallet = wallets.find(w => w.id === fromId);

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="h-10 w-10 rounded-2xl flex items-center justify-center" style={{ background: `${C.accent}14` }}>
          <ArrowLeftRight className="h-5 w-5" style={{ color: C.accent }} />
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: C.text }}>Manual Transfer</h3>
          <p className="text-xs" style={{ color: C.textMuted }}>Move funds between any two wallets — no fees</p>
        </div>
      </div>
      <div className="mb-4">
        <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: C.textMuted }}>From</label>
        <select value={fromId} onChange={e => setFromId(e.target.value)} className="w-full rounded-xl px-4 py-3 text-sm font-medium" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg, color: C.text }}>
          {wallets.map(w => <option key={w.id} value={w.id}>{w.label} — {fmtUGX(w.balance)}</option>)}
        </select>
      </div>
      <div className="mb-4">
        <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: C.textMuted }}>To</label>
        <select value={toId} onChange={e => setToId(e.target.value)} className="w-full rounded-xl px-4 py-3 text-sm font-medium" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg, color: C.text }}>
          <option value="">Select destination…</option>
          {destOptions.map(w => <option key={w.id} value={w.id}>{w.label} — {fmtUGX(w.balance)}</option>)}
        </select>
      </div>
      <div className="mb-6">
        <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: C.textMuted }}>Amount (UGX)</label>
        <input type="number" value={amount || ""} onChange={e => setAmount(Number(e.target.value))} placeholder="0"
          className="w-full rounded-xl px-4 py-3 text-2xl font-bold" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg, color: C.text }} />
        <div className="flex gap-2 mt-2">
          {[5000, 20000, 50000, 100000].map(v => (
            <button key={v} onClick={() => setAmount(v)} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: C.bg, color: C.textMuted, border: `1px solid ${C.cardBorder}` }}>{fmtUGX(v)}</button>
          ))}
        </div>
      </div>
      <button onClick={() => onExecute(fromId, toId, amount)} disabled={submitting || !toId || amount < 500}
        className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition disabled:opacity-50"
        style={{ background: "#1C1917" }}>
        {submitting ? "Transferring…" : `Transfer ${fmtUGX(amount)}`}
      </button>
    </div>
  );
}

// ── Statement Dialog ──
function StatementDialog({ open, onClose, ledger, parentWallet, students }: {
  open: boolean; onClose: () => void;
  ledger: Ledger[]; parentWallet: ParentWallet | null | undefined; students: Student[];
}) {
  if (!open) return null;

  const totalIn = ledger.filter(t => t.direction === "credit").reduce((s, t) => s + t.amount_ugx, 0);
  const totalOut = ledger.filter(t => t.direction === "debit").reduce((s, t) => s + t.amount_ugx, 0);
  const net = totalIn - totalOut;

  const handleDownload = () => {
    const lines = [
      "TriSphere Pay — Family Wallet Statement",
      `Generated: ${new Date().toLocaleString("en-UG")}`,
      "",
      `Opening Balance: UGX 0`,
      `Total Credits: UGX ${totalIn.toLocaleString()}`,
      `Total Debits: UGX ${totalOut.toLocaleString()}`,
      `Closing Balance: UGX ${(parentWallet?.balance_ugx ?? 0).toLocaleString()}`,
      `Net Flow: UGX ${net.toLocaleString()}`,
      "",
      "Date,Description,Direction,Amount,Reference",
      ...ledger.map(t =>
        `${new Date(t.occurred_at).toISOString().slice(0,10)},"${t.title}",${t.direction},${t.amount_ugx},${t.ref ?? ""}`
      ),
    ].join("\n");
    const blob = new Blob([lines], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trisphere-statement-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Statement downloaded");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
        className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 p-6 pb-4 bg-white border-b" style={{ borderColor: C.cardBorder }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-2xl flex items-center justify-center" style={{ background: `${C.green}14` }}>
                <FileText className="h-5 w-5" style={{ color: C.green }} />
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ color: C.text }}>Wallet Statement</h3>
                <p className="text-xs" style={{ color: C.textMuted }}>{ledger.length} transactions</p>
              </div>
            </div>
            <button onClick={handleDownload}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white transition hover:scale-105"
              style={{ background: C.green }}>
              <Download className="h-3.5 w-3.5" /> Download CSV
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-3 gap-3 p-6">
          <div className="rounded-2xl p-4" style={{ background: `${C.green}08` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Total In</p>
            <p className="text-lg font-bold mt-1" style={{ color: C.green }}>{fmtUGX(totalIn)}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: `${C.orange}08` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Total Out</p>
            <p className="text-lg font-bold mt-1" style={{ color: C.orange }}>{fmtUGX(totalOut)}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: `${C.accent}08` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Net Flow</p>
            <p className="text-lg font-bold mt-1" style={{ color: net >= 0 ? C.green : C.red }}>{net >= 0 ? "+" : "−"}{fmtUGX(Math.abs(net))}</p>
          </div>
        </div>

        {/* Transaction table */}
        <div className="px-6 pb-6">
          <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.cardBorder}` }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: C.bg }}>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Date</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Description</th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {ledger.slice(0, 50).map(t => (
                  <tr key={t.id} className="border-t" style={{ borderColor: C.cardBorder }}>
                    <td className="px-4 py-3 text-xs" style={{ color: C.textMuted }}>{shortDateTime(t.occurred_at)}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium" style={{ color: C.text }}>{t.title}</p>
                      {t.subtitle && <p className="text-[10px]" style={{ color: C.textLight }}>{t.subtitle}</p>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-bold" style={{ color: t.direction === "credit" ? C.green : C.text }}>
                        {t.direction === "credit" ? "+" : "−"}{fmtUGX(t.amount_ugx)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Pay Fees Dialog ──
function PayFeesDialog({ open, onClose, students, fees, walletId }: {
  open: boolean; onClose: () => void;
  students: Student[]; fees: FeeTerm[]; walletId?: string;
}) {
  if (!open) return null;
  const payStudents = students.map(s => ({
    id: s.id, full_name: s.full_name, initials: s.initials, color_hex: childColor(students, s.id),
  }));
  const payFees = fees.map(f => ({ student_id: f.student_id, total_ugx: f.total_ugx, paid_ugx: f.paid_ugx }));

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
        className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-2xl flex items-center justify-center" style={{ background: `${C.green}14` }}>
              <Receipt className="h-5 w-5" style={{ color: C.green }} />
            </div>
            <div>
              <h3 className="text-lg font-bold" style={{ color: C.text }}>Pay School Fees</h3>
              <p className="text-xs" style={{ color: C.textMuted }}>Via TriSphere Pay — Mobile Money</p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            {students.map(s => {
              const childFees = fees.filter(f => f.student_id === s.id);
              const outstanding = childFees.reduce((a, f) => a + Math.max(0, f.total_ugx - f.paid_ugx), 0);
              const color = childColor(students, s.id);
              return (
                <div key={s.id} className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: C.bg }}>
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: color }}>
                    {s.full_name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: C.text }}>{s.full_name}</p>
                    <p className="text-xs" style={{ color: outstanding > 0 ? C.red : C.green }}>
                      {outstanding > 0 ? `${fmtUGX(outstanding)} outstanding` : "Fees fully paid"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <TriSpherePayTopUp walletId={walletId} students={payStudents} fees={payFees} trigger={
            <button className="w-full py-3.5 rounded-[10px] text-sm font-semibold text-white transition hover:opacity-90"
              style={{ background: "#1C1917" }}>
              <Receipt className="h-4 w-4 inline mr-2" />Pay via TriSphere Pay
            </button>
          } />
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Main FinanceSummary ──
export function FinanceSummary({ students, fees, parentWallet, ledger, studentWallets, walletId }: {
  students: Student[]; fees: FeeTerm[]; parentWallet: ParentWallet | null | undefined;
  ledger: Ledger[]; studentWallets: StudentWallet[]; walletId?: string;
}) {
  const kidsTotal = studentWallets.reduce((s, w) => s + (w.balance_ugx ?? 0), 0);
  const parentBal = parentWallet?.balance_ugx ?? 0;
  const familyTotal = parentBal + kidsTotal;
  const totalDue = fees.reduce((s, f) => s + Math.max(0, f.total_ugx - f.paid_ugx), 0);
  const totalPaid = fees.reduce((s, f) => s + f.paid_ugx, 0);
  const totalFees = fees.reduce((s, f) => s + f.total_ugx, 0);
  const feeProgress = totalFees > 0 ? (totalPaid / totalFees) * 100 : 0;
  const animatedFamily = useCountUp(familyTotal, 1200);

  const [showTransfer, setShowTransfer] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [showPayFees, setShowPayFees] = useState(false);
  const [sparkHover, setSparkHover] = useState<number | null>(null);

  // Spending by category — derived from ledger
  const categories = useMemo(() => {
    const catMap = new Map<string, number>();
    const kindColors: Record<string, { color: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }> }> = {
      fees: { color: C.accent, icon: FeesIcon },
      transport: { color: C.orange, icon: CalendarIcon },
      canteen: { color: C.amber, icon: ReceiptIcon },
      uniform: { color: C.purple, icon: ReceiptIcon },
      bus: { color: C.orange, icon: CalendarIcon },
      topup: { color: C.green, icon: WalletIcon },
      spend: { color: C.teal, icon: ReceiptIcon },
      transfer: { color: C.blue, icon: ArrowLeftRight },
    };
    ledger.filter(t => t.direction === "debit").forEach(t => {
      const key = t.kind ?? "other";
      catMap.set(key, (catMap.get(key) ?? 0) + t.amount_ugx);
    });
    const totalSpent = Array.from(catMap.values()).reduce((s, v) => s + v, 0);
    return Array.from(catMap.entries()).map(([kind, value]) => {
      const meta = kindColors[kind] ?? { color: C.teal, icon: ReceiptIcon };
      return {
        label: kind.charAt(0).toUpperCase() + kind.slice(1),
        value,
        pct: totalSpent > 0 ? (value / totalSpent) * 100 : 0,
        color: meta.color,
        icon: meta.icon,
      };
    }).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [ledger]);

  // Sparkline — 30-day spending from ledger
  const sparkData = useMemo(() => {
    const days: number[] = Array(30).fill(0);
    const now = new Date(); now.setHours(23, 59, 59, 999);
    ledger.forEach(t => {
      if (t.direction !== "debit") return;
      const d = new Date(t.occurred_at);
      const diff = Math.floor((now.getTime() - d.getTime()) / 86400000);
      if (diff >= 0 && diff < 30) days[29 - diff] += t.amount_ugx;
    });
    const max = Math.max(...days, 1);
    return days.map((y, i) => ({ x: i, y: (y / max) * 100 }));
  }, [ledger]);

  const sparkMax = useMemo(() => {
    const days: number[] = Array(30).fill(0);
    const now = new Date(); now.setHours(23, 59, 59, 999);
    ledger.forEach(t => {
      if (t.direction !== "debit") return;
      const d = new Date(t.occurred_at);
      const diff = Math.floor((now.getTime() - d.getTime()) / 86400000);
      if (diff >= 0 && diff < 30) days[29 - diff] += t.amount_ugx;
    });
    return days.reduce((s, v) => s + v, 0);
  }, [ledger]);

  // Monthly + daily spend from ledger
  const monthSpend = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return ledger.filter(t => t.direction === "debit" && new Date(t.occurred_at) >= monthStart).reduce((s, t) => s + t.amount_ugx, 0);
  }, [ledger]);

  const avgDaily = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const days = Math.max(1, Math.ceil((now.getTime() - monthStart.getTime()) / 86400000));
    return Math.round(monthSpend / days);
  }, [monthSpend]);

  // TriSphere Pay trigger
  const payStudents = students.map(s => ({
    id: s.id, full_name: s.full_name, initials: s.initials, color_hex: childColor(students, s.id),
  }));
  const payFees = fees.map(f => ({ student_id: f.student_id, total_ugx: f.total_ugx, paid_ugx: f.paid_ugx }));

  return (
    <div className="space-y-6 sm:space-y-8 max-w-5xl mx-auto">
      {/* ═════════ HERO ═════════ */}
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="relative overflow-hidden rounded-[20px] shadow-float"
        style={{ background: "#1C1917" }}
      >
        {/* Ambient glows */}
        <motion.div
          className="absolute -right-32 -top-32 h-96 w-96 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(216,169,60,0.18) 0%, transparent 65%)" }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.7, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(52,199,89,0.12) 0%, transparent 65%)" }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

        <div className="relative px-6 sm:px-10 lg:px-14 py-10 sm:py-14">
          {/* Top row */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <WalletIcon className="h-4 w-4" accent="#fff" style={{ color: "rgba(255,255,255,0.7)" }} />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">Family Treasury</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowStatement(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white/80 transition hover:bg-white/10">
                <Download className="h-3.5 w-3.5" /> Statement
              </button>
            </div>
          </div>

          {/* Main balance */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">{fmtUGX(animatedFamily)}</h1>
          <p className="mt-4 text-lg sm:text-xl text-white/60 font-light max-w-xl">Total family balance across all wallets</p>

          {/* Balance breakdown */}
          <div className="flex flex-wrap gap-3 mt-8">
            {[
              { label: "Parent Wallet", value: fmtUGX(parentBal), color: "text-white", icon: Wallet },
              { label: "Children Wallets", value: fmtUGX(kidsTotal), color: "text-white", icon: CreditCard },
              { label: "Fees Due", value: totalDue > 0 ? fmtUGX(totalDue) : "All Clear", color: totalDue > 0 ? "text-red-400" : "text-white", icon: AlertCircle },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                whileHover={{ y: -2, scale: 1.02 }}
                className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-white/[0.08] backdrop-blur-2xl border border-white/10"
              >
                <s.icon className={`h-4 w-4 ${s.color}`} />
                <div>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">{s.label}</p>
                  <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 mt-6">
            <TriSpherePayTopUp walletId={walletId} students={payStudents} fees={payFees} trigger={
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} className="flex items-center gap-2 px-5 py-2.5 rounded-[10px] text-sm font-semibold text-white" style={{ background: "rgba(255,255,255,0.12)" }}>
                <PlusIcon className="h-4 w-4" />Top Up
              </motion.button>
            } />
            <motion.button onClick={() => setShowTransfer(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} className="flex items-center gap-2 px-5 py-2.5 rounded-[10px] text-sm font-semibold bg-white/10 backdrop-blur-xl border border-white/10 text-white transition hover:bg-white/15">
              <ArrowLeftRight className="h-4 w-4" />Transfer
            </motion.button>
            <motion.button onClick={() => setShowPayFees(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} className="flex items-center gap-2 px-5 py-2.5 rounded-[10px] text-sm font-semibold bg-white/10 backdrop-blur-xl border border-white/10 text-white transition hover:bg-white/15">
              <Receipt className="h-4 w-4" />Pay Fees
            </motion.button>
          </div>

          {/* 30-day spending sparkline */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" style={{ color: "rgba(255,255,255,0.5)" }} />
                <span className="text-xs font-semibold text-white/50">30-Day Spending Trend</span>
              </div>
              <span className="text-xs font-bold" style={{ color: "rgba(255,255,255,0.7)" }}>{fmtUGX(sparkMax)}</span>
            </div>
            <div className="relative" onMouseLeave={() => setSparkHover(null)}>
              <svg viewBox="0 0 300 60" className="w-full h-14">
                <defs>
                  <linearGradient id="finSparkGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[12, 30, 48].map((y) => (
                  <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" strokeDasharray="2 4" />
                ))}
                {sparkData.length > 1 && (
                  <>
                    <motion.polyline
                      fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                      points={sparkData.map((d) => `${d.x * 10},${60 - (d.y / 100) * 50}`).join(" ")}
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5, ease: "easeOut" }}
                    />
                    <polygon fill="url(#finSparkGrad)" points={`0,60 ${sparkData.map((d) => `${d.x * 10},${60 - (d.y / 100) * 50}`).join(" ")} 300,60`} />
                  </>
                )}
                {sparkHover !== null && sparkData[sparkHover] && (
                  <circle cx={sparkHover * 10} cy={60 - (sparkData[sparkHover].y / 100) * 50} r="3" fill="#fff" stroke="#1C1917" strokeWidth="2" />
                )}
                {sparkData.map((d, i) => (
                  <rect key={i} x={i * 10 - 5} y="0" width="10" height="60" fill="transparent" onMouseEnter={() => setSparkHover(i)} />
                ))}
              </svg>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ═════════ STAT TILES ═════════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Paid", value: fmtUGX(totalPaid), sub: `${Math.round(feeProgress)}% of total`, icon: CheckCircle2, color: C.green, gradient: `linear-gradient(135deg, ${C.green}, #D4B89A)` },
          { label: "Outstanding", value: totalDue > 0 ? fmtUGX(totalDue) : "All Clear", sub: totalDue > 0 ? "Action needed" : "No fees due", icon: AlertCircle, color: totalDue > 0 ? C.red : C.green, gradient: `linear-gradient(135deg, ${totalDue > 0 ? C.red : C.green}, ${totalDue > 0 ? "#A68A5A" : "#D4B89A"})` },
          { label: "This Month", value: fmtUGX(monthSpend), sub: "Spending", icon: TrendingDown, color: C.orange, gradient: `linear-gradient(135deg, ${C.orange}, #A68A5A)` },
          { label: "Avg / Day", value: fmtUGX(avgDaily), sub: "Daily average", icon: CalendarClock, color: C.accent, gradient: ACCENT_GRADIENT },
        ].map((stat, i) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <StatTile label={stat.label} value={stat.value} sub={stat.sub} icon={stat.icon} accent={stat.color} gradient={stat.gradient} />
          </motion.div>
        ))}
      </div>

      {/* ═════════ FEE STATUS ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
        <PremiumCard>
          <SectionHeader title="Fee Status" subtitle={`${students.length} ${students.length === 1 ? "child" : "children"} · ${fmtUGX(totalPaid)} of ${fmtUGX(totalFees)} paid`} icon={ReceiptIcon} iconBg={C.blueLight}
            action={<div className="flex items-center gap-3"><ProgressRing value={feeProgress} size={56} stroke={5} color={C.green} /></div>}
          />
          <div className="p-5 sm:p-6 space-y-5">
            {students.map((s, i) => {
              const childFees = fees.filter(f => f.student_id === s.id);
              const childTotal = childFees.reduce((a, f) => a + f.total_ugx, 0);
              const childPaid = childFees.reduce((a, f) => a + f.paid_ugx, 0);
              const childDue = Math.max(0, childTotal - childPaid);
              const pct = childTotal > 0 ? (childPaid / childTotal) * 100 : 100;
              const color = childColor(students, s.id);
              return (
                <motion.div key={s.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.08 }} className="flex items-center gap-4">
                  <Avatar name={s.full_name} color={color} size={48} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <p className="text-sm font-semibold truncate" style={{ color: C.text }}>{s.full_name}</p>
                      <p className="text-xs font-bold shrink-0" style={{ color: childDue > 0 ? C.red : C.green }}>{childDue > 0 ? `${fmtUGX(childDue)} due` : "Paid"}</p>
                    </div>
                    <MiniBar value={childPaid} max={childTotal} color={pct >= 100 ? C.green : pct >= 50 ? C.amber : C.red} />
                    <p className="text-[10px] mt-1" style={{ color: C.textMuted }}>{Math.round(pct)}% · {fmtUGX(childPaid)} of {fmtUGX(childTotal)}</p>
                  </div>
                  {childDue > 0 && (
                    <TriSpherePayTopUp walletId={walletId} students={payStudents} fees={payFees} trigger={
                      <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="h-9 px-4 rounded-xl text-xs font-semibold text-white shrink-0" style={{ background: color }}>Pay</motion.button>
                    } />
                  )}
                </motion.div>
              );
            })}
          </div>
        </PremiumCard>
      </motion.div>

      {/* ═════════ SPENDING BY CATEGORY ═════════ */}
      {categories.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }}>
          <PremiumCard>
            <SectionHeader title="Spending by Category" subtitle="Derived from your transaction history" icon={PieChart} iconBg={C.purpleLight} />
            <div className="p-5 sm:p-6">
              <div className="space-y-3">
                {categories.map((cat, i) => (
                  <motion.div key={cat.label} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.06 }} className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: `${cat.color}15` }}>
                      <cat.icon className="h-4 w-4" style={{ color: cat.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-xs font-semibold" style={{ color: C.text }}>{cat.label}</p>
                        <p className="text-xs font-bold" style={{ color: cat.color }}>{fmtUGX(cat.value)}</p>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: C.bg }}>
                        <motion.div initial={{ width: 0 }} animate={{ width: `${cat.pct}%` }} transition={{ delay: 0.25 + i * 0.06, duration: 0.6, ease: "easeOut" }} className="h-full rounded-full" style={{ background: cat.color }} />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </PremiumCard>
        </motion.div>
      )}

      {/* ═════════ PAYMENT METHOD ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
        <div className="mb-4">
          <h3 className="text-xl font-bold tracking-tight" style={{ color: C.text }}>Payment Method</h3>
          <p className="text-sm mt-0.5" style={{ color: C.textMuted }}>All payments are processed through TriSphere Pay</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.06, ease: EASE }}
            whileHover={{ y: -4 }}
            className="relative rounded-2xl border p-5 text-left transition-all shadow-premium bg-white overflow-hidden"
            style={{ borderColor: `${C.accent}40` }}
          >
            <div className="absolute inset-0 opacity-5" style={{ background: `radial-gradient(circle at 50% 0%, ${C.accent}, transparent 70%)` }} />
            <div className="relative">
              <div className="flex items-start justify-between mb-3">
                <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${C.accent}, #A42022)` }}>
                  <Wallet className="h-6 w-6 text-white" />
                </div>
                <Pill color={C.green}>Active</Pill>
              </div>
              <p className="text-sm font-bold tracking-tight" style={{ color: C.text }}>TriSphere Pay</p>
              <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>MTN MoMo & Airtel Money</p>
              <div className="flex items-center gap-2 mt-3">
                <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: C.amberLight }}>
                  <Smartphone className="h-3 w-3" style={{ color: C.amber }} />
                </div>
                <span className="text-[10px] font-semibold" style={{ color: C.textMuted }}>MTN</span>
                <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: C.redLight }}>
                  <Smartphone className="h-3 w-3" style={{ color: C.red }} />
                </div>
                <span className="text-[10px] font-semibold" style={{ color: C.textMuted }}>Airtel</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.12, ease: EASE }}
            whileHover={{ y: -4 }}
            className="relative rounded-2xl border-2 border-dashed p-5 text-left transition-all bg-white/50 overflow-hidden"
            style={{ borderColor: C.cardBorder }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: C.bg }}>
                <Plus className="h-6 w-6" style={{ color: C.textLight }} />
              </div>
            </div>
            <p className="text-sm font-bold tracking-tight" style={{ color: C.textMuted }}>Add Payment Method</p>
            <p className="text-xs mt-0.5" style={{ color: C.textLight }}>Coming soon — bank transfer & cards</p>
          </motion.div>
        </div>
      </motion.div>

      {/* ═════════ BILLING SCHEDULE — from fee terms ═════════ */}
      {fees.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.5 }}>
          <PremiumCard>
            <SectionHeader title="Billing Schedule" subtitle="Upcoming payment deadlines from fee records" icon={CalendarIcon} iconBg={C.purpleLight} />
            <div className="divide-y divide-[#E2D6C0]/60">
              {fees.map((f, i) => {
                const student = students.find(s => s.id === f.student_id);
                const outstanding = Math.max(0, f.total_ugx - f.paid_ugx);
                if (outstanding <= 0) return null;
                const dueDate = f.next_due ? new Date(f.next_due) : null;
                const daysLeft = dueDate ? Math.ceil((dueDate.getTime() - Date.now()) / 86400000) : null;
                const urgent = daysLeft !== null && daysLeft <= 7;
                return (
                  <motion.div key={f.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.06 }} whileHover={{ x: 4 }} className="group flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-[#EDE4D4]/50 transition-colors cursor-pointer">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: urgent ? C.redLight : C.blueLight }}>
                      {urgent ? <AlertCircle className="h-5 w-5" style={{ color: C.red }} /> : <CalendarIcon className="h-5 w-5" style={{ color: C.blue }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: C.text }}>{f.term_label}</p>
                      <p className="text-xs" style={{ color: C.textMuted }}>{student?.full_name ?? "—"} · Due {dueDate ? dueDate.toLocaleDateString("en-UG", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold" style={{ color: C.text }}>{fmtUGX(outstanding)}</p>
                      {daysLeft !== null && daysLeft > 0 ? (
                        urgent ? <Pill color={C.red}>Due in {daysLeft}d</Pill> : <span className="text-[10px] font-medium" style={{ color: C.textMuted }}>{daysLeft} days</span>
                      ) : null}
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: C.textMuted }} />
                  </motion.div>
                );
              })}
            </div>
          </PremiumCard>
        </motion.div>
      )}

      <FamilyTreasury />

      {/* Dialogs */}
      <AnimatePresence>
        <TransferDialog open={showTransfer} onClose={() => setShowTransfer(false)} parentWallet={parentWallet} students={students} studentWallets={studentWallets} fees={fees} />
      </AnimatePresence>
      <AnimatePresence>
        <StatementDialog open={showStatement} onClose={() => setShowStatement(false)} ledger={ledger} parentWallet={parentWallet} students={students} />
      </AnimatePresence>
      <AnimatePresence>
        <PayFeesDialog open={showPayFees} onClose={() => setShowPayFees(false)} students={students} fees={fees} walletId={walletId} />
      </AnimatePresence>
    </div>
  );
}

export function MoneyView({ ledger }: { ledger: Ledger[] }) {
  const [filter, setFilter] = useState<"all" | "credit" | "debit">("all");
  const [search, setSearch] = useState("");

  const items = ledger;

  const filtered = items.filter(t => {
    const matchesFilter = filter === "all" || t.direction === filter;
    const matchesSearch = !search || t.title.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const totalIn = items.filter(t => t.direction === "credit").reduce((s, t) => s + t.amount_ugx, 0);
  const totalOut = items.filter(t => t.direction === "debit").reduce((s, t) => s + t.amount_ugx, 0);

  if (items.length === 0) {
    return (
      <PremiumCard>
        <SectionHeader title="Recent Transactions" subtitle="All wallet activity" icon={GrowthIcon} iconBg={C.greenLight} />
        <div className="p-12 text-center">
          <Wallet className="h-12 w-12 mx-auto mb-3" style={{ color: C.textLight }} />
          <p className="text-sm font-medium" style={{ color: C.textMuted }}>No transactions yet</p>
          <p className="text-xs mt-1" style={{ color: C.textLight }}>Your transaction history will appear here once you make your first top-up or payment</p>
        </div>
      </PremiumCard>
    );
  }

  return (
    <PremiumCard>
      <SectionHeader title="Recent Transactions" subtitle="All wallet activity" icon={GrowthIcon} iconBg={C.greenLight}
        action={
          <div className="flex items-center gap-2">
            {(["all", "credit", "debit"] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} className="px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition"
                style={filter === f ? { background: C.accent, color: "white" } : { background: C.bg, color: C.textMuted }}>
                {f === "credit" ? "In" : f === "debit" ? "Out" : "All"}
              </button>
            ))}
          </div>
        }
      />
      {/* Search bar */}
      <div className="px-5 sm:px-6 pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: C.textMuted }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search transactions…" className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{ border: `1px solid ${C.cardBorder}`, background: C.bg, color: C.text }} />
        </div>
      </div>
      <div className="max-h-[500px] overflow-y-auto divide-y divide-[#E2D6C0]/60">
        {filtered.map((t, i) => (
          <motion.div key={t.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} whileHover={{ x: 4 }} className="group flex items-center gap-4 px-5 sm:px-6 py-3.5 hover:bg-[#EDE4D4]/50 transition-colors">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: t.direction === "credit" ? C.greenLight : C.orangeLight }}>
              {t.direction === "credit" ? <ArrowDownRight className="h-5 w-5" style={{ color: C.green }} /> : <ArrowUpRight className="h-5 w-5" style={{ color: C.orange }} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: C.text }}>{t.title}</p>
              <div className="flex items-center gap-2">
                <p className="text-xs" style={{ color: C.textMuted }}>{shortDateTime(t.occurred_at)}</p>
                {t.subtitle && <><span className="h-1 w-1 rounded-full" style={{ background: C.textLight }} /><span className="text-xs" style={{ color: C.textMuted }}>{t.subtitle}</span></>}
              </div>
            </div>
            <p className="text-sm font-bold shrink-0" style={{ color: t.direction === "credit" ? C.green : C.textMid }}>
              {t.direction === "credit" ? "+" : "−"}{fmtUGX(t.amount_ugx)}
            </p>
          </motion.div>
        ))}
      </div>
      {/* Footer summary */}
      <div className="px-5 sm:px-6 py-3.5 border-t border-[#E2D6C0]/60 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: C.green }} />
            <span className="text-[11px] font-medium" style={{ color: C.textMuted }}>In: {fmtUGX(totalIn)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: C.orange }} />
            <span className="text-[11px] font-medium" style={{ color: C.textMuted }}>Out: {fmtUGX(totalOut)}</span>
          </div>
        </div>
        <span className="text-[11px] font-bold" style={{ color: totalIn - totalOut >= 0 ? C.green : C.red }}>Net {totalIn - totalOut >= 0 ? "+" : "−"}{fmtUGX(Math.abs(totalIn - totalOut))}</span>
      </div>
    </PremiumCard>
  );
}
